import { motion } from "framer-motion";
import { 
  Users, 
  Swords, 
  Brain, 
  Ban, 
  Check, 
  X 
} from "lucide-react";

const differentiators = [
  {
    icon: Users,
    title: "Buyer-Side Only",
    description: "We never work for sellers. No conflicts. No softened findings. Pure advocacy for the acquirer.",
  },
  {
    icon: Swords,
    title: "Adversarial by Design",
    description: "We're paid to find problems, not to make deals close. Our incentives align with protecting your investment.",
  },
  {
    icon: Brain,
    title: "AI-Augmented Experts",
    description: "Not 'AI theatre'—real engineers using intelligent tooling to accelerate deep analysis.",
  },
  {
    icon: Ban,
    title: "No Success Fees",
    description: "We're paid to tell the truth, not to help transactions complete. Independence is non-negotiable.",
  },
];

const comparisons = [
  { aspect: "Buyer alignment", provenance: true, others: false },
  { aspect: "Engineering-led analysis", provenance: true, others: false },
  { aspect: "Adversarial methodology", provenance: true, others: false },
  { aspect: "No success fees", provenance: true, others: false },
  { aspect: "IP provenance tracking", provenance: true, others: false },
  { aspect: "Seller relationship incentives", provenance: false, others: true },
];

export const WhyWeWin = () => {
  return (
    <section className="relative py-24 bg-card/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Why PROVENANCE
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            Built different. By design.
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Traditional advisors have conflicts. We don't. Our model ensures 
            complete alignment with buyer interests.
          </p>
        </motion.div>

        {/* Differentiators Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {differentiators.map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="rounded-2xl border border-border bg-background p-6"
            >
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <item.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="rounded-2xl border border-border bg-background p-8"
        >
          <h3 className="font-display text-xl font-semibold text-center mb-8">
            PROVENANCE vs traditional advisors
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 text-muted-foreground font-medium">Capability</th>
                  <th className="text-center py-4 px-4">
                    <span className="text-primary font-bold">PROVENANCE</span>
                  </th>
                  <th className="text-center py-4 px-4">
                    <span className="text-muted-foreground">Traditional advisors</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row) => (
                  <tr key={row.aspect} className="border-b border-border/50">
                    <td className="py-4 px-4 text-sm">{row.aspect}</td>
                    <td className="py-4 px-4 text-center">
                      {row.provenance ? (
                        <Check className="h-5 w-5 text-score-excellent mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {row.others ? (
                        <Check className="h-5 w-5 text-score-warning mx-auto" />
                      ) : (
                        <X className="h-5 w-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
